/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { Text, View } from '@az/starc-ui';
import { Rating } from '@/components/Rating/Rating';
import { useFeatureFlag } from '@/features/globalConfig';
import { useLocale } from '@/hooks/useLocale';
import { SyndicatedReviewBadge } from '../../../SyndicatedReviewBadge/SyndicatedReviewBadge';
import { ReviewFeedbackActions } from '../../../ReviewFeedbackActions/ReviewFeedbackActions';
import { formatReviewSubmissionTime } from '../../../../utils/formatReviewSubmissionTime';
import { type ProductReview } from '../../../../interface';
import styles from './styles.module.scss';
import { RecommendationStatus } from '../RecommendationStatus/RecommendationStatus';
import { ReviewAuthorInfo } from '../ReviewAuthorInfo/ReviewAuthorInfo';
import { ReviewBadges } from '../ReviewBadges/ReviewBadges';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';

export type CustomerReviewProps = {
  review: ProductReview;
};

export function Review({ review }: CustomerReviewProps) {
  const locale = useLocale();
  const bvAPIEnabled = useFeatureFlag('BV_API_ENABLED') === 'true';

  return (
    <FlexGrid flexDirection="column" gap={{ sm: 2, md: 4 }}>
      <View
        className={styles.starRating}
        attributes={{ 'data-testid': 'rating' }}
        direction={'row'}
        align={'center'}
        justify={'space-between'}
      >
        <Rating value={review.rating} />

        <View align="center">
          <Text
            as="span"
            className={styles.secondaryText}
            attributes={{ 'data-testid': 'submittedTime' }}
          >
            {formatReviewSubmissionTime(review.submittedTimestamp, locale) || review.timeAgo}
          </Text>
        </View>
      </View>

      <View align="start">
        <Text
          as="span"
          weight="bold"
          className={styles.reviewTitle}
          attributes={{ 'data-testid': 'title' }}
        >
          {review.title}
        </Text>
      </View>

      <FlexGrid gap={{ sm: 1, md: 2 }} flexDirection="column">
        <ReviewAuthorInfo
          reviewer={review.reviewer}
          vehicleAnswer={review.vehicleAnswer}
          vehicleQuestion={review.vehicleQuestion}
        />

        {(review.incentivizedReview || review.verifiedPurchase) && (
          <ReviewBadges
            incentivizedReview={review.incentivizedReview}
            verifiedPurchase={review.verifiedPurchase}
          />
        )}
      </FlexGrid>

      <View>
        <Text as="p" attributes={{ 'data-testid': 'body' }} className={styles.reviewBodyText}>
          {review.body}
        </Text>
      </View>

      <ul className={styles.contentList}>
        <li>
          <RecommendationStatus recommend={review.recommend} textClassName={styles.secondaryText} />
        </li>

        {bvAPIEnabled && !review.syndicatedReviewFlag && (
          <li>
            <ReviewFeedbackActions
              reviewId={review.id}
              positiveFeedbackCount={review.positiveFeedbackCount}
              negativeFeedbackCount={review.negativeFeedbackCount}
              questionClassName={styles.modalFeedbackQuestion}
            />
          </li>
        )}

        {review.syndicatedReviewFlag && (
          <li>
            <SyndicatedReviewBadge
              name={review.syndicatedClientName}
              logo={review.syndicatedClientLogoUrl}
            />
          </li>
        )}
      </ul>
    </FlexGrid>
  );
}
